import { useEffect, useState } from 'react';
import { Search, Plus, Eye, MapPin, Phone, Calendar, Package, Truck, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

export default function PickupsPage() {
  const navigate = useNavigate();
  const [pickups, setPickups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, pending: 0, assigned: 0, completed: 0 });
  const [selectedPickup, setSelectedPickup] = useState<any | null>(null);

  useEffect(() => { fetchPickups(); }, []);

  const fetchPickups = async () => {
    try {
      const response = await api.get('/api/pickups');
      const data = response.data.data || [];
      setPickups(Array.isArray(data) ? data : (data.items || []));
      const items = Array.isArray(data) ? data : (data.items || []);
      setStats({
        total: items.length,
        pending: items.filter(p => (p.status || '').toLowerCase().includes('pending')).length,
        assigned: items.filter(p => (p.status || '').toLowerCase().includes('assign')).length,
        completed: items.filter(p => (p.status || '').toLowerCase().includes('complete') || (p.status || '').toLowerCase().includes('pickup')).length
      });
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s.includes('pending')) return 'bg-yellow-100 text-yellow-800';
    if (s.includes('assign')) return 'bg-blue-100 text-blue-800';
    if (s.includes('complete') || s.includes('pickup')) return 'bg-green-100 text-green-800';
    if (s.includes('cancel')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr: string) => dateStr ? new Date(dateStr).toLocaleString('ar-EG') : '-';

  const getStatusLabel = (status: string) => {
    const value = (status || '').toLowerCase();
    if (value.includes('pending') || value.includes('pending approval')) return 'قيد الانتظار';
    if (value.includes('assign')) return 'تم التعيين';
    if (value.includes('complete') || value.includes('pickup')) return 'تم الاستلام';
    if (value.includes('cancel')) return 'ملغى';
    if (value.includes('reject')) return 'مرفوض';
    return status || 'غير محددة';
  };

  const filteredPickups = pickups.filter(p => {
    const matchesSearch = (p.pickupNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (p.merchantName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || (p.status || '').toLowerCase().includes(statusFilter);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              طلبات البيك اب
            </h1>
            <p className="text-gray-600 mt-2">إدارة طلبات استلام الشحنات من التجار</p>
          </div>
          <Button onClick={() => navigate('/pickups/new')} className="bg-[#1e40af] hover:bg-[#1e3a8a]">
            <Plus className="w-4 h-4 ml-2" />طلب جديد
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><Package className="w-6 h-6 text-purple-600" /></div><div><p className="text-sm text-gray-600">إجمالي الطلبات</p><p className="text-2xl font-bold">{stats.total}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center"><Calendar className="w-6 h-6 text-yellow-600" /></div><div><p className="text-sm text-gray-600">الطلبات المعلقة</p><p className="text-2xl font-bold text-yellow-700">{stats.pending}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><Truck className="w-6 h-6 text-blue-600" /></div><div><p className="text-sm text-gray-600">الطلبات المنجزة</p><p className="text-2xl font-bold text-blue-700">{stats.assigned}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><Package className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-600">الملغاة</p><p className="text-2xl font-bold text-green-700">{stats.completed}</p></div></CardContent></Card>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="بحث..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-10 pl-4 py-2 border rounded-lg" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
                <option value="all">رقم الطلب</option>
                <option value="pending">الطلبات المعلقة</option>
                <option value="assigned">الطلبات المنجزة</option>
                <option value="completed">مكتمل</option>
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-purple-50 border-b border-purple-200">
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <MapPin className="w-5 h-5" />قائمة الطلبات ({filteredPickups.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (<div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div><p>جاري التحميل...</p></div>) :
            filteredPickups.length === 0 ? (<div className="text-center py-12"><MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">لا توجد طلبات بيك أب</p></div>) :
            (<div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-right p-3 font-semibold text-sm">رقم البيك اب</th>
                <th className="text-right p-3 font-semibold text-sm">التاجر</th>
                <th className="text-right p-3 font-semibold text-sm">العنوان</th>
                <th className="text-right p-3 font-semibold text-sm">الحالة</th>
                <th className="text-right p-3 font-semibold text-sm">التاريخ</th>
                <th className="text-right p-3 font-semibold text-sm">الإجراءات</th>
              </tr></thead>
              <tbody>
                {filteredPickups.map((pickup) => (
                  <tr key={pickup.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-mono font-bold text-purple-700">{pickup.pickupNumber}</td>
                    <td className="p-3"><div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-500" /><span className="font-semibold">{pickup.contactName || pickup.merchantName || '-'}</span></div></td>
                    <td className="p-3 text-sm max-w-xs truncate"><div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" />{pickup.address || '-'}</div></td>
                    <td className="p-3"><Badge className={getStatusColor(pickup.status)}>{getStatusLabel(pickup.status)}</Badge></td>
                    <td className="p-3 text-sm text-gray-600">{formatDate(pickup.createdAt || pickup.scheduledDate)}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          title="عرض تفاصيل طلب الالتقاط"
                          onClick={() => setSelectedPickup(pickup)}
                          className="border-orange-200 text-orange-700 hover:bg-orange-50"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          title={pickup.phone || pickup.merchantPhone ? 'الاتصال بالتاجر' : 'رقم الهاتف غير متوفر'}
                          disabled={!pickup.phone && !pickup.merchantPhone}
                          onClick={() => {
                            const phone = pickup.phone || pickup.merchantPhone;
                            if (phone) window.location.href = `tel:${phone}`;
                          }}
                          className="border-orange-200 text-orange-700 hover:bg-orange-50 disabled:opacity-40"
                        >
                          <Phone className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>)}
          </CardContent>
        </Card>
      </div>

      {selectedPickup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" dir="rtl">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-orange-100 overflow-hidden">
            <div className="flex items-center justify-between bg-orange-600 px-5 py-4 text-white">
              <div>
                <h2 className="text-lg font-bold">تفاصيل طلب الالتقاط</h2>
                <p className="text-xs text-orange-100">#{selectedPickup.pickupNumber || selectedPickup.id}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPickup(null)}
                className="rounded-lg px-3 py-1 text-2xl leading-none hover:bg-orange-700"
                aria-label="إغلاق"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 text-sm">
              <div><span className="block text-gray-500 mb-1">التاجر</span><strong>{selectedPickup.contactName || selectedPickup.merchantName || 'غير محدد'}</strong></div>
              <div><span className="block text-gray-500 mb-1">الهاتف</span><strong dir="ltr">{selectedPickup.phone || selectedPickup.merchantPhone || 'غير متوفر'}</strong></div>
              <div><span className="block text-gray-500 mb-1">العنوان</span><strong>{selectedPickup.address || selectedPickup.pickupAddress || 'غير محدد'}</strong></div>
              <div><span className="block text-gray-500 mb-1">المندوب</span><strong>{selectedPickup.delegateName || 'غير محدد'}</strong></div>
              <div><span className="block text-gray-500 mb-1">التاريخ</span><strong>{formatDate(selectedPickup.createdAt || selectedPickup.scheduledDate || selectedPickup.pickupDate)}</strong></div>
              <div><span className="block text-gray-500 mb-1">الحالة</span><strong>{getStatusLabel(selectedPickup.status)}</strong></div>
              {selectedPickup.notes && <div className="sm:col-span-2"><span className="block text-gray-500 mb-1">الملاحظات</span><p className="rounded-lg bg-orange-50 p-3 text-orange-900">{selectedPickup.notes}</p></div>}
            </div>
            <div className="flex justify-end gap-2 border-t border-orange-100 bg-orange-50/40 px-5 py-3">
              <button type="button" onClick={() => setSelectedPickup(null)} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">إغلاق</button>
              {(selectedPickup.phone || selectedPickup.merchantPhone) && <button type="button" onClick={() => { const phone = selectedPickup.phone || selectedPickup.merchantPhone; window.location.href = `tel:${phone}`; }} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"><Phone className="inline-block ml-1 h-4 w-4" /> اتصال</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
